import * as React from 'react';
import { FC, useMemo, useState } from 'react';
import { Menu, MenuItem, ListSubheader } from '@material-ui/core';
import { Tooltip } from '@mui/material';
import { IconButton } from '@material-ui/core';
import CodeIcon from '@material-ui/icons/Code';
import { TextInputField, TextInputFieldProps } from './TextInputField';
import { useTranslations } from '../../../store/translations/hooks';
import { TK } from '../../../store/translations/translationKeys';
import { useGeneralSettings } from '../../../store/settings/hooks';
import { getPlaceholderItems, PlaceholderItem } from '../../../shared/placeholders';

export type { PlaceholderItem };

export const insertPlaceholderAtCursor = (
    key: string,
    inputId: string,
    currentValue: string,
    onChange?: (v: string) => void,
) => {
    const el = document.getElementById(inputId) as HTMLTextAreaElement | HTMLInputElement | null;
    if (!el) { onChange?.(`${currentValue}{${key}}`); return; }
    const start = el.selectionStart ?? currentValue.length;
    const end = el.selectionEnd ?? currentValue.length;
    onChange?.(currentValue.substring(0, start) + `{${key}}` + currentValue.substring(end));
    requestAnimationFrame(() => {
        el.focus();
        const pos = start + key.length + 2;
        el.setSelectionRange(pos, pos);
    });
};

interface PlaceholderMenuProps {
    anchorEl: HTMLElement | null;
    onClose: () => void;
    onInsert: (key: string) => void;
}

export const PlaceholderMenu: FC<PlaceholderMenuProps> = ({ anchorEl, onClose, onInsert }) => {
    const t = useTranslations();
    const generalSettings = useGeneralSettings();
    const placeholders = useMemo(() => getPlaceholderItems(t, generalSettings?.clientProperties), [t, generalSettings?.clientProperties]);
    const sections = useMemo(() => placeholders.reduce<{ section: string; items: PlaceholderItem[] }[]>((acc, item) => {
        const section = item.section ?? '';
        const existing = acc.find(g => g.section === section);
        if (existing) existing.items.push(item);
        else acc.push({ section, items: [item] });
        return acc;
    }, []), [placeholders]);

    return (
        <Menu
            anchorEl={anchorEl}
            open={!!anchorEl}
            onClose={onClose}
            PaperProps={{ style: { maxHeight: 400, minWidth: 240 } }}
        >
            {sections.map(({ section, items }) => [
                section && (
                    <ListSubheader key={`header-${section}`} style={{ lineHeight: '2rem', fontSize: '0.7rem', fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase', color: 'var(--text-muted)', backgroundColor: 'var(--bg-light)' }}>
                        {section}
                    </ListSubheader>
                ),
                ...items.map(item => (
                    <MenuItem key={item.key} onClick={() => onInsert(item.key)} style={{ fontSize: '0.875rem' }}>
                        {item.label}
                    </MenuItem>
                )),
            ])}
        </Menu>
    );
};

export interface PlaceholderTextFieldProps extends TextInputFieldProps {
    id: string;
}

export const PlaceholderTextField: React.FC<PlaceholderTextFieldProps> = ({ id, value, onChange, ...rest }) => {
    const t = useTranslations();
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

    const trigger = (
        <span style={rest.type === 'textarea' ? { alignSelf: 'flex-start', paddingTop: '2px' } : undefined}>
            <Tooltip title={t(TK.addDynamicField)}>
                <IconButton size="small" onClick={e => setAnchorEl(e.currentTarget)}>
                    <CodeIcon style={{ fontSize: '1rem' }} />
                </IconButton>
            </Tooltip>
        </span>
    );

    return (
        <>
            <TextInputField id={id} value={value} onChange={onChange} endAdornment={trigger} {...rest} />
            <PlaceholderMenu
                anchorEl={anchorEl}
                onClose={() => setAnchorEl(null)}
                onInsert={(key) => {
                    setAnchorEl(null);
                    insertPlaceholderAtCursor(key, `${id}-input`, String(value ?? ''), onChange);
                }}
            />
        </>
    );
};
